import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { ShareroomMonetisationFinishModal } from "@ui/components/group/shareroom/shareroomMonetisationFinishModal";
import { row, stack } from "@ui/components/layout";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { useSettingEditorController } from "@ui/pages/group/shareroom/dashboard/settingEditorPage/settingEditorController/settingEditorControllerContext";

interface SettingEditorNavigationProps extends React.HTMLAttributes<HTMLDivElement> {
	initialShareroom: Group;
}

export const SettingEditorNavigation: React.FC<SettingEditorNavigationProps> = ({ initialShareroom, ...htmlProps }) => {
	const controller = useSettingEditorController();
	const step = useObservable(controller.step);
	const isPresentationValid = useObservable(controller.isPresentationValid);
	const isPricingValid = useObservable(controller.isPricingValid);
	const { currency, price, subscriptionMode } = useObservable(controller.group);

	const history = useHistory();
	const { createModal } = useModalTools();

	const { groupService } = useServices();

	const openFinishModal = (shareroom: Group): void => {
		createModal((close) => <ShareroomMonetisationFinishModal shareroom={shareroom} close={close} />);
	};

	const { run, loading } = useAsync(async () => {
		try {
			const newShareroom = await groupService.saveShareroomPricing(
				{ subscriptionMode, priceInUserCurrency: price, userCurrency: currency },
				initialShareroom.id
			);
			openFinishModal(newShareroom);
		} catch (error: unknown) {
			if (typeof error === "object" && error !== null && "code" in error && error.code === 400)
				createToast(
					I18n.t("shareroom.dashboard.pricing.update.forbidden.title"),
					I18n.t("shareroom.dashboard.pricing.update.forbidden.message"),
					"error"
				);
		}
	}, [currency, price, subscriptionMode]);

	return (
		<Container {...htmlProps}>
			{loading ? (
				<ClipLoader />
			) : step === "PRICING" ? (
				<LoadingButton loading={loading} disabled={!isPricingValid} onClick={() => run()}>
					{I18n.t("save")}
				</LoadingButton>
			) : (
				<PrimaryButton
					onClick={() => (!isPresentationValid ? history.push(Path.Shareroom(initialShareroom)) : controller.next())}
				>
					{I18n.t(!isPresentationValid ? "shareroom.dashboard.check.fill_about_page" : "next")}
				</PrimaryButton>
			)}
		</Container>
	);
};

const Container = styled.div`
	@media ${desktop} {
		${row(12, "flex-end", "center")};
	}
	@media ${mobile} {
		${stack(10, "center", "center", "reverse")};
	}
`;
